// Terminal text is untrusted input (CLAUDE.md). The daemon cleans display text already; the phone
// strips again before anything reaches the DOM, and marks what it removed so nothing is hidden silently.

// ESC-led sequences: CSI (ESC [ ... final), OSC (ESC ] ... BEL or ST), and two-character escapes.
// eslint-disable-next-line no-control-regex
const ANSI = /\u001b\[[0-?]*[ -/]*[@-~]|\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)|\u001b[@-Z\\-_]|\u009b[0-?]*[ -/]*[@-~]/g;
// C0 and C1 controls and DEL, except tab and newline. A lone CR counts; CRLF is normalised to LF first.
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f]/g;
/**
 * Characters that render as nothing (or as a blank) and so can hide what a command does, matched by Unicode
 * property so new code points in these classes are covered too (security review M1): format characters (Cf: zero-width
 * and bidi controls, soft hyphen, BOM, invisible operators, the tag block), Default_Ignorable_Code_Point (U+034F,
 * Hangul fillers, variation selectors, U+E0000-E0FFF), Variation_Selector, the line and paragraph separators, and the
 * explicit code points the review named. Identical to HIDDEN in packages/daemon/src/screen/strip.ts; the shared
 * vectors live in packages/daemon/test/fixtures/hidden-characters.json.
 */
const HIDDEN = /[\p{Cf}\p{Default_Ignorable_Code_Point}\p{Variation_Selector}\p{Zl}\p{Zp}\u034F\u061C\u115F\u1160\u3164\uFFA0\u{E0000}-\u{E007F}]/gu;

export type Sanitised = { text: string; removed: number; invisible: number; control: number };

/** Strips escape codes, control and invisible characters. Keeps newlines and tabs. Counts are code points, not UTF-16 units. */
export function sanitise(input: string): Sanitised {
  let control = 0;
  let invisible = 0;
  const text = input
    .replace(/\r\n/g, "\n")
    .replace(ANSI, () => (control++, ""))
    .replace(HIDDEN, () => (invisible++, ""))
    .replace(CONTROL, () => (control++, ""));
  return { text, removed: invisible + control, invisible, control };
}

function markers(invisible: number, control: number): string {
  const out: string[] = [];
  if (invisible) out.push(`[${invisible} invisible character${invisible === 1 ? "" : "s"} removed]`);
  if (control) out.push(`[${control} control character${control === 1 ? "" : "s"} removed]`);
  return out.join(" ");
}

/** Cuts to `max` code points, never through a surrogate pair. */
function cap(text: string, max: number): string {
  const chars = [...text];
  return chars.length > max ? `${chars.slice(0, max - 1).join("")}…` : text;
}

/** For display: the cleaned text plus a visible marker for each kind of removal (SPEC 3.11). */
export function displayText(input: string | undefined): string {
  if (!input) return "";
  const { text, invisible, control } = sanitise(input);
  const marks = markers(invisible, control);
  return marks ? `${text}\n${marks}` : text;
}

/**
 * A name, label or heading from the daemon or the relay (security review 2, S3): cleaned, folded onto one line and
 * capped, with the removal markers kept. Anything that isn't a string becomes "".
 */
export function displayLine(input: unknown, max = 120): string {
  if (typeof input !== "string" || !input) return "";
  const { text, invisible, control } = sanitise(input);
  return [cap(text.replace(/\s+/g, " ").trim(), max), markers(invisible, control)].filter(Boolean).join(" ");
}

/** Like displayLine, for text that may run to several lines: a question, a description, a notice. */
export function displayBlock(input: unknown, max = 2000): string {
  if (typeof input !== "string" || !input) return "";
  const { text, invisible, control } = sanitise(input);
  return [cap(text.trim(), max), markers(invisible, control)].filter(Boolean).join("\n");
}

/** Splits text on the visible change markers so they can be styled: "[masked]", "[1,240 characters omitted, …]". */
export type TextPart = { kind: "text" | "marker"; value: string };
const MARKER = /(\[(?:masked|[\d,]+ characters? omitted[^\]]*|\d+ (?:invisible|control) characters? removed|\d+ possible credentials? masked|The command was cut for display[^\]]*)\])/g;

export function splitMarkers(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(MARKER)) {
    const i = m.index ?? 0;
    if (i > last) parts.push({ kind: "text", value: text.slice(last, i) });
    parts.push({ kind: "marker", value: m[0] });
    last = i + m[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}
